// Audio format whitelist for upload. Both iOS and Android can decode every
// format in this list without additional codecs, so any rejection here would
// be a runtime failure on at least one platform.
//
// WAVs additionally need a PCM-encoded payload — the OS decoders we target
// don't all handle ADPCM, A-law, μ-law, etc. `isPcmWav` parses the WAV
// header to check the `fmt ` chunk's `wFormatTag`.

type FormatSpec = {
    extensions: string[];
    mimeTypes: string[];
};

const FORMAT_SPECS: FormatSpec[] = [
    {
        extensions: [".mp3"],
        mimeTypes: ["audio/mpeg", "audio/mp3"]
    },
    {
        extensions: [".aac", ".m4a", ".mp4"],
        mimeTypes: ["audio/aac", "audio/mp4", "audio/x-m4a", "audio/m4a"]
    },
    {
        extensions: [".flac"],
        mimeTypes: ["audio/flac", "audio/x-flac"]
    },
    {
        extensions: [".wav"],
        mimeTypes: ["audio/wav", "audio/x-wav", "audio/wave"]
    }
];

export const SUPPORTED_AUDIO_ACCEPT = Array.from(new Set(
    FORMAT_SPECS.flatMap((spec) => [...spec.extensions, ...spec.mimeTypes])
)).join(",");

export const SUPPORTED_AUDIO_DESCRIPTION = "MP3, AAC (M4A/MP4), FLAC, or WAV (PCM)";

export const isSupportedAudioFile = (file: File): boolean => {
    const name = (file.name || "").toLowerCase();
    const type = (file.type || "").toLowerCase();
    return FORMAT_SPECS.some((spec) => {
        if (spec.extensions.some((ext) => name.endsWith(ext))) {
            return true;
        }
        if (type && spec.mimeTypes.includes(type)) {
            return true;
        }
        return false;
    });
};

const WAV_EXTENSIONS = [".wav"];
const WAV_MIME_TYPES = ["audio/wav", "audio/x-wav", "audio/wave"];

export const isWavFile = (file: File): boolean => {
    const name = (file.name || "").toLowerCase();
    const type = (file.type || "").toLowerCase();
    return WAV_EXTENSIONS.some((ext) => name.endsWith(ext))
        || (!!type && WAV_MIME_TYPES.includes(type));
};

// Parse the first chunk of a WAV file to verify the `fmt ` chunk reports PCM
// (`wFormatTag == 0x0001`, or `0xFFFE` extensible with PCM SubFormat GUID).
// Reads only the first ~4 KB of the file — enough to find the `fmt ` chunk in
// every well-formed WAV we've seen.
const WAV_HEADER_READ_BYTES = 4096;
const WAVE_FORMAT_PCM = 0x0001;
const WAVE_FORMAT_EXTENSIBLE = 0xFFFE;

const readFourCC = (view: DataView, offset: number) => {
    if (offset + 4 > view.byteLength) {
        return "";
    }
    return String.fromCharCode(
        view.getUint8(offset),
        view.getUint8(offset + 1),
        view.getUint8(offset + 2),
        view.getUint8(offset + 3)
    );
};

export const isPcmWav = async (file: File): Promise<boolean> => {
    try {
        const slice = file.slice(0, WAV_HEADER_READ_BYTES);
        const buffer = await slice.arrayBuffer();
        const view = new DataView(buffer);
        if (view.byteLength < 12) {
            return false;
        }
        if (readFourCC(view, 0) !== "RIFF" || readFourCC(view, 8) !== "WAVE") {
            return false;
        }
        let offset = 12;
        while (offset + 8 <= view.byteLength) {
            const chunkId = readFourCC(view, offset);
            const chunkSize = view.getUint32(offset + 4, true);
            const payloadStart = offset + 8;
            if (chunkId === "fmt ") {
                if (payloadStart + 2 > view.byteLength) {
                    return false;
                }
                const formatTag = view.getUint16(payloadStart, true);
                if (formatTag === WAVE_FORMAT_PCM) {
                    return true;
                }
                if (formatTag === WAVE_FORMAT_EXTENSIBLE) {
                    // SubFormat GUID lives at payload + 24 (16 bytes).
                    // PCM SubFormat first 4 bytes (little-endian) = 0x00000001.
                    if (payloadStart + 24 + 4 > view.byteLength) {
                        return false;
                    }
                    const subFormat = view.getUint32(payloadStart + 24, true);
                    return subFormat === WAVE_FORMAT_PCM;
                }
                return false;
            }
            // Chunks are word-aligned: pad odd-sized chunks by 1.
            offset = payloadStart + chunkSize + (chunkSize % 2);
        }
        return false;
    } catch (error) {
        console.warn("Unable to parse WAV header", error);
        return false;
    }
};
